import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'
import { readStoredConsent } from '@/lib/cookie-consent-status'
import type { CrossSellTarget } from '@/lib/santa-video/video-moments'

type PublicChatEventKey = Extract<keyof typeof PRODUCT_EVENTS, `PUBLIC_CHAT_${string}`>
export type PublicChatEvent = (typeof PRODUCT_EVENTS)[PublicChatEventKey]

/**
 * Propiedades del funnel del chat público (AC2.4): solo vocabulario cerrado y
 * números. Nunca el nombre del niño ni el texto de un mensaje.
 */
export type PublicChatEventProperties = {
  locale?: string
  n?: number
  placement?: PublicChatSignupPlacement
  cta?: 'signup' | 'call'
}

/** Desde dónde se va al alta: la landing, los popups del chat (micrófono, llamar) o la tarjeta de cierre. */
export type PublicChatSignupPlacement = 'landing' | 'voice_prompt' | 'call_prompt' | 'closing_card'

/**
 * Única salida de los eventos `public_chat_*` (C4). Con la analítica rechazada
 * no se captura nada: ni se envía (aunque PostHog arrancase forzado) ni se
 * encola para enviarse si el padre acepta después en la misma página. Sin
 * decisión todavía sí se captura: la cola de `instrumentation-client` solo se
 * vacía si llega a aceptar.
 */
export function capturePublicChatEvent(event: PublicChatEvent, properties: PublicChatEventProperties): void {
  if (analyticsRejected()) return
  captureProductEvent(event, properties)
}

/** Adónde lleva la comparativa de la landing: la videollamada o la tienda del vídeo. */
export type PublicChatCrossSellTarget = Extract<CrossSellTarget, 'call' | 'shop'>

/**
 * El clic en un cross-sell de la comparativa (AC6.3): `video_cross_sell_clicked`
 * con `placement` public-chat, bajo la misma regla de consentimiento que el
 * funnel del chat.
 */
export function capturePublicChatCrossSell(target: PublicChatCrossSellTarget): void {
  if (analyticsRejected()) return
  captureProductEvent(PRODUCT_EVENTS.VIDEO_CROSS_SELL_CLICKED, { product: 'video', placement: 'public-chat', target })
}

function analyticsRejected(): boolean {
  return readStoredConsent() === 'rejected'
}
